import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators,} from '@angular/forms';
import {IAutoCompleteItem, ListItem} from '@canvas/four-r-digital';
import {IAccount, IUnitOfMeasure} from '../../common/dtos';
import {IAccountAutoCompleteItem} from './add-account-entry/dtos/account-auto-complete-item';
import {IAccountEntry} from './dtos/account-entry';
import {IAccountRequest} from './dtos/create-account-request';

/**
 * Creates a new Account FormGroup
 * @param accountDetails Optional CreateAccountRequest to pre-populate the form group
 * @returns FormGroup
 */
export function CreateAccountFormGroup(
  accountDetails?: Partial<IAccountRequest>,
  isEditable = true,
): FormGroup {
  const disabled = !isEditable;

  return new FormGroup({
    balanceDirection: new FormControl(
      FormControlValue(accountDetails?.balanceDirection || undefined, disabled),
      [],
    ),
    baseCurrency: new FormControl(
      FormControlValue(accountDetails?.baseCurrency || '', disabled),
      [],
    ),
    description: new FormControl(
      FormControlValue(accountDetails?.description || ''),
      [],
    ),
    name: new FormControl(FormControlValue(accountDetails?.name || ''), []),
    ownerId: new FormControl(
      FormControlValue(accountDetails?.ownerId || '', disabled),
      [],
    ),
    ownerName: new FormControl(
      FormControlValue(accountDetails?.ownerName || '', disabled),
      [],
    ),
    status: new FormControl(
      FormControlValue(accountDetails?.status || undefined),
      [],
    ),
    type: new FormControl(
      FormControlValue(accountDetails?.type || undefined, disabled),
      [],
    ),
    unitOfMeasureId: new FormControl(
      FormControlValue(accountDetails?.unitOfMeasureId || undefined, disabled),
      [],
    ),
    ownerType: new FormControl(
      FormControlValue(accountDetails?.ownerType || undefined, disabled),
      [],
    ),
    accountNumber: new FormControl({
      value: accountDetails?.accountNumber || undefined,
      disabled: true,
    }),
    reconciliationEnabled: new FormControl({
      value: accountDetails?.reconciliationEnabled || false,
      disabled: false,
    }),
    reconciliationType: new FormControl(
      FormControlValue(accountDetails?.reconciliationType ?? null),
    ),
    withdrawalEnabled: new FormControl({
      value: accountDetails?.withdrawalEnabled || false,
      disabled: false,
    }),
  });
}

export function CreateAccountEntryFormGroup(
  entry?: IAccountEntry,
  debitAccount?: IAccount,
): FormGroup {
  const debitAccountRequiredValidators =
    debitAccount === undefined
      ? [Validators.required, createAutoCompleteValidator()]
      : [];

  return new FormGroup({
    amount: new FormControl(entry?.amount, [
      Validators.required,
      createNonZeroValueValidator(),
    ]),
    reference: new FormControl(entry?.reference, [Validators.maxLength(50)]),
    description: new FormControl(entry?.description, [
      Validators.required,
      Validators.maxLength(50),
    ]),
    linkedTransactionId: new FormControl(entry?.description, [
      Validators.maxLength(50),
    ]),
    creditAccountItem: new FormControl(
      {} as IAccountAutoCompleteItem | undefined,
      [Validators.required, createAutoCompleteValidator()],
    ),
    debitAccountItem: new FormControl(
      {} as IAccountAutoCompleteItem | undefined,
      debitAccountRequiredValidators,
    ),
  });
}

export function FormControlValue(value: unknown, disabled = false) {
  return {
    value,
    disabled,
  };
}

/**
 * Convert units of measure to an array of ListItem0
 * @param unitsfMeasure All the units of measure to convert
 * @returns An Array of ListItem
 */
export function UnitsOfMeasureToListItems(
  unitsfMeasure: IUnitOfMeasure[],
): ListItem[] {
  return unitsfMeasure.map((l) => {
    return { id: l.id, text: l.name } as ListItem;
  });
}

/**
 * Get a list (ListIem array) of unit of measure, filtered against optional allowed units
 * @param allUnitsOfMeasure
 * @param allowedUnitsOfMeasureCodes
 * @returns An array of List  Item
 */
export function GetUnitsOfMeasure(
  allUnitsOfMeasure: IUnitOfMeasure[],
  allowedUnitsOfMeasureCodes?: string[],
): ListItem[] {
  if (!allowedUnitsOfMeasureCodes) {
    return UnitsOfMeasureToListItems(allUnitsOfMeasure);
  }

  const filteredList = allUnitsOfMeasure.filter(
    (uom) =>
      allowedUnitsOfMeasureCodes.find(
        (ac) => ac?.toUpperCase() === uom.shortCode?.toUpperCase(),
      ) !== undefined,
  );

  return UnitsOfMeasureToListItems(filteredList);
}

/**
 * ListItem[] Zlookup on ID
 * @param itemsList
 * @param ids
 * @returns ListItem[]
 */
export function findListItemsForIds(
  itemsList: ListItem[],
  ids: number[] | undefined,
): ListItem[] {
  if (ids) {
    return itemsList.filter((a) => ids.find((at) => at === a.id) !== undefined);
  }
  return itemsList;
}

// export {createAutoCompleteValidator, createNonZeroValueValidator};
export function createNonZeroValueValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;
    if (val !== undefined && val !== null) {
      const floatVal = parseFloat(val.toString());
      if (isNaN(floatVal)) {
        return { nonZeroValue: true };
      }
      if (floatVal === 0) {
        return { nonZeroValue: true };
      }

      return null;
    }

    return { nonZeroValue: true };
  };
}

export function createAutoCompleteValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const val = control.value;
    if (val !== undefined && val !== null) {
      const autoCompleteItem = val as IAutoCompleteItem;
      if (
        autoCompleteItem &&
        (!autoCompleteItem.id ||
          !autoCompleteItem.text ||
          autoCompleteItem.text.length === 0)
      ) {
        return { nonZeroValue: true };
      }

      return null;
    }

    return { nonZeroValue: true };
  };
}
